<template>
  <Modal
    fullbelowsm
    :title="!editData ? $t('projects.ai_providers.create') : $t('projects.ai_providers.edit')"
    :primary-button="$t('save')"
    :secondary-button="$t('cancel')"
    :secondary-button-disabled="loading"
    :primary-button-loading="loading"
    :primary-button-disabled="!provider || saveDisabled"
    @primary="onSaveClick"
    @secondary="onCancelClick"
    @opened="onModalOpen"
  >
    <div class="w-600 flex flex-col">
      <div class="w-full flex flex-col justify-start mt-3 py-3 px-10 flex-shrink-0">
        <ValidationObserver ref="validationObserver" v-slot="{ errors }" class="w-full">
          <FormLabel :label="$t('projects.ai_providers.provider')" />
          <div class="font-500">{{ providerName }}</div>
          <FormInput v-if="hasUrlField" v-model="url" :label="$t('projects.ai_providers.url')" name="url" :error="errors.url" rules="required" class="mt-2" />
          <FormInput v-model="key" :label="$t('projects.ai_providers.key')" name="key" :error="errors.key" rules="required" class="mt-2" />
          <FormInput v-if="hasAccountIdField" v-model="account_id" :label="$t('projects.ai_providers.account_id')" name="url" :error="errors.account_id" rules="required" class="mt-2" />
          <FormInput v-if="hasAccountEmailField" v-model="account_email" :label="$t('projects.ai_providers.account_email')" name="url" :error="errors.account_email" rules="required" class="mt-2" />

          <div class="flex items-center justify-between mt-4">
            <FormLabel :label="$t('projects.ai_providers.enabled')" class="!mb-0" />
            <Toggle v-model="enabled" dense />
          </div>
          <div class="text-11 text-gray-500 mt-1">{{ $t('projects.ai_providers.enabled_help') }}</div>

          <!-- Self-hosted (LM Studio): discover models live, select + load one, snapshot its capabilities. -->
          <template v-if="isDynamicModels">
            <div class="flex items-center gap-3 mt-4">
              <PillButton outlined :text="discovering ? 'Loading…' : 'Test connection / Load models'" :disabled="!canDiscover || discovering" :loading="discovering" @click="onDiscoverClick" />
              <span v-if="discoverStatus" class="text-12" :class="statusClass">{{ discoverStatusLabel }}</span>
            </div>

            <template v-if="lmModels.length">
              <FormSelect
                :label="$t('projects.ai_providers.model')"
                v-model="selectedModel"
                :options="modelOptions"
                :reduce="(o) => o.value"
                auto-flip
                class="w-full mt-3"
                @update:modelValue="onModelPick"
              >
                <template #option="opt">
                  <div class="flex items-center justify-between w-full gap-2">
                    <span>{{ opt.label }}</span>
                    <Badge v-if="opt.loaded" text="loaded" success small />
                  </div>
                </template>
              </FormSelect>
              <div class="text-11 text-gray-400 mt-1">Loaded models are ready immediately. Selecting an unloaded model will unload the current one and load it — this can take a minute or two.</div>

              <div class="flex items-center gap-3 mt-3">
                <PillButton primary outlined :text="selectButtonText" :loading="selecting" :disabled="!selectedModel || selecting" @click="onSelectModelClick" />
                <span v-if="loadPhase !== 'idle' && !loadError" class="text-12" :class="loadPhase === 'loaded' ? 'text-green-600' : 'text-gray-500'">{{ loadPhaseLabel }}</span>
              </div>
              <div v-if="loadError" class="text-12 text-red-500 mt-2">{{ loadError }}</div>
            </template>

            <div v-if="modelMeta" class="mt-3 p-3 rounded-8 bg-gray-50 border border-gray-100">
              <div class="text-12 font-600">{{ modelMeta.name }}</div>
              <div class="text-11 text-gray-500 mt-1">Context length: {{ (modelMeta.context_length || modelMeta.max_context_length || 0).toLocaleString() }} tokens</div>
              <div class="flex flex-wrap gap-1 mt-2">
                <span v-for="cap in capabilityChips" :key="cap" class="text-10 px-2 py-1 rounded-full bg-gray-100 text-gray-600 border border-gray-200">{{ cap }}</span>
                <span v-if="!capabilityChips.length" class="text-10 text-gray-400">No advertised capabilities</span>
              </div>
            </div>
          </template>
        </ValidationObserver>
      </div>
    </div>

    <template #footer>
      <div class="w-full flex py-4 px-10">
        <PillButton v-if="editData" error :text="$t('delete')" :disabled="loading" @click="onDeleteClick" />
        <PillButton outlined :text="$t('cancel')" :disabled="loading" class="ml-auto" @click="onCancelClick" />
        <PillButton primary :loading="loading" :disabled="loading || saveDisabled" class="ml-2" :text="$t('save')" outlined @click="onSaveClick" />
      </div>
    </template>
  </Modal>
</template>

<script>
import { mapActions, mapState } from 'vuex';
import { AI_FEATURES } from '@/constants';
import { apiLMStudioModels, apiLMStudioSelectModel, apiLMStudioModelMeta } from '@/helpers/api';

// Large models (100B+) can take several minutes to load on the deployment.
const LOAD_TIMEOUT_MS = 6 * 60 * 1000;
const LOAD_POLL_MS = 3000;

export default {
  name: 'CreateAIProviderModal',
  props: {
    projectId: {
      type: String,
      required: true,
    },
    provider: {
      type: String,
    },
    providerName: {
      type: String,
    },
    editData: {
      type: Object,
    },
    onDelete: {
      type: Function,
    },
  },
  data() {
    return {
      loading: false,
      key: '',
      url: '',
      account_id: '',
      account_email: '',
      enabled: true,
      // LM Studio (dynamic models)
      discovering: false,
      selecting: false,
      discoverStatus: '',
      discoverMessage: '',
      lmModels: [],
      selectedModel: '',
      modelMeta: null,
      loadPhase: 'idle', // idle | unloading | loading | loaded
      loadError: '',
      loadErrorReason: '',
      loadActive: false,
      loadDone: false,
      loadDeadline: 0,
      loadToken: 0, // identifies the current load attempt; stale callbacks are ignored
      pollTimer: null,
    };
  },
  created() {
    if (this.editData) {
      const { key, enabled, url, account_id, account_email, model, model_meta } = this.editData.config || {};
      this.key = key;
      this.enabled = enabled === true;
      this.url = url;
      this.account_id = account_id;
      this.account_email = account_email;
      if (model) this.selectedModel = model;
      if (model_meta) {
        this.modelMeta = model_meta;
        this.loadPhase = 'loaded';
      }
    }
  },
  beforeUnmount() {
    this.stopPolling();
  },
  computed: {
    ...mapState(['availableAIProviders']),
    hasUrlField() {
      return this.availableAIProviders[this.provider]?.url === true;
    },
    hasAccountIdField() {
      return this.availableAIProviders[this.provider]?.account_id === true;
    },
    hasAccountEmailField() {
      return this.availableAIProviders[this.provider]?.account_email === true;
    },
    isDynamicModels() {
      return this.availableAIProviders[this.provider]?.dynamic_models === true;
    },
    canDiscover() {
      return !!this.url && !!this.key;
    },
    modelOptions() {
      return this.lmModels.filter((m) => m.type === 'llm').map((m) => ({ label: m.name, value: m.id, loaded: m.loaded }));
    },
    isSelectedLoaded() {
      const m = this.lmModels.find((x) => x.id === this.selectedModel);
      return !!m && m.loaded;
    },
    selectButtonText() {
      return this.isSelectedLoaded ? 'Use this model' : 'Select & load model';
    },
    statusClass() {
      if (this.discoverStatus === 'Connected') return 'text-green-600';
      if (this.discoverStatus === 'No Loaded Model') return 'text-amber-600';
      return 'text-red-500';
    },
    discoverStatusLabel() {
      if (this.discoverStatus === 'No Loaded Model') return 'Connected — no model loaded yet';
      if (this.discoverStatus === 'Connected') return 'Connected';
      return this.discoverMessage || this.discoverStatus;
    },
    loadPhaseLabel() {
      return { unloading: 'Unloading current model…', loading: 'Loading model… (large models can take a few minutes)', loaded: 'Model loaded' }[this.loadPhase] || '';
    },
    capabilityChips() {
      if (!this.modelMeta) return [];
      const f = this.modelMeta.features || [];
      const chips = [];
      if (f.includes(AI_FEATURES.FUNCTION_CALLING)) chips.push('Tool Calling');
      if (f.includes(AI_FEATURES.STRUCTURED_OUTPUT)) chips.push('Structured Output');
      if (f.includes(AI_FEATURES.REASONING)) chips.push('Reasoning');
      if ((this.modelMeta.input_modality || []).includes('Image')) chips.push('Vision');
      return chips;
    },
    saveDisabled() {
      if (!this.isDynamicModels) return false;
      // Must have a capability snapshot matching the chosen model.
      return this.selecting || this.discovering || !this.modelMeta || this.modelMeta.id !== this.selectedModel;
    },
  },
  methods: {
    ...mapActions(['showToastMessage', 'createProjectAIProvider', 'updateProjectAIProvider']),
    onModalOpen() {
      this.$refs?.name?.focus();
    },
    async onDeleteClick() {
      if (this.onDelete) {
        await this.onDelete();
      }
    },
    async onDiscoverClick() {
      if (!this.canDiscover) return;
      this.discovering = true;
      const res = await apiLMStudioModels({ project_id: this.projectId, url: this.url, key: this.key });
      this.discovering = false;
      if (res.status === 200 && res.data && !res.data.error) {
        this.lmModels = res.data.models || [];
        this.discoverStatus = res.data.status || '';
        this.discoverMessage = res.data.message || '';
        const loaded = this.lmModels.find((m) => m.loaded && m.type === 'llm');
        if (loaded) this.selectedModel = loaded.id;
        else if (!this.selectedModel) this.selectedModel = (this.lmModels.find((m) => m.type === 'llm') || {}).id || '';
      } else {
        this.discoverStatus = 'Error';
        this.discoverMessage = res.data?.message || 'Failed to reach the LM Studio server.';
        this.showToastMessage({ title: this.discoverMessage, type: 'error' });
      }
    },
    onModelPick() {
      // Changing the selection cancels any in-flight load (its callbacks become stale
      // via the token) and resets the phase to match the newly chosen model.
      this.loadToken += 1;
      this.stopPolling();
      this.selecting = false;
      this.loadError = '';
      this.loadPhase = this.modelMeta && this.modelMeta.id === this.selectedModel ? 'loaded' : 'idle';
    },
    stopPolling() {
      this.loadActive = false;
      if (this.pollTimer) {
        clearTimeout(this.pollTimer);
        this.pollTimer = null;
      }
    },
    onSelectModelClick() {
      if (!this.selectedModel || this.selecting) return;
      this.loadToken += 1;
      const token = this.loadToken;
      const model = this.selectedModel;
      this.selecting = true;
      this.loadDone = false;
      this.loadActive = true;
      this.loadError = '';
      this.loadErrorReason = '';
      this.loadDeadline = Date.now() + LOAD_TIMEOUT_MS;
      this.loadPhase = this.isSelectedLoaded ? 'loading' : 'unloading';

      // Trigger unload + load on the server. For large models this request may be cut
      // by the tunnel's edge timeout — that's fine, LM Studio keeps loading and we
      // detect completion by polling model_meta below, so we don't block the UI on it.
      apiLMStudioSelectModel({ project_id: this.projectId, url: this.url, key: this.key, model })
        .then((res) => {
          if (token !== this.loadToken) return; // superseded by a newer attempt — ignore
          if (res?.status === 200 && res.data && !res.data.error && res.data.model_meta) {
            this.applyLoaded(res.data.model_meta);
            return;
          }
          const msg = typeof res?.data?.message === 'string' ? res.data.message : '';
          // A cut-off (edge timeout) means the model is still loading — keep polling.
          // A definitive error (e.g. out of memory, model unavailable) fails fast.
          if (!msg || /524|timeout|timed out|gateway|network/i.test(msg)) {
            this.loadErrorReason = msg;
          } else {
            this.failLoad(token, msg);
          }
        })
        .catch(() => {});

      this.pollLoad(token, model);
    },
    // Poll the read-only model_meta endpoint until the model reports loaded, or the deadline passes.
    async pollLoad(token, model) {
      if (token !== this.loadToken || !this.loadActive) return;
      if (Date.now() > this.loadDeadline) {
        this.failLoad(token, this.loadErrorReason || 'Loading the model timed out. It may still be loading on the server — please try again in a moment.');
        return;
      }
      let res = null;
      try {
        res = await apiLMStudioModelMeta({ project_id: this.projectId, url: this.url, key: this.key, model });
      } catch (e) {
        res = null;
      }
      if (token !== this.loadToken || !this.loadActive) return;
      if (res?.status === 200 && res.data && !res.data.error) {
        if (res.data.loaded && res.data.model_meta) {
          this.applyLoaded(res.data.model_meta);
          return;
        }
        this.loadPhase = res.data.loadedModelId ? 'unloading' : 'loading';
      }
      this.pollTimer = setTimeout(() => this.pollLoad(token, model), LOAD_POLL_MS);
    },
    applyLoaded(meta) {
      if (this.loadDone) return;
      this.loadDone = true;
      this.stopPolling();
      this.modelMeta = meta;
      this.loadPhase = 'loaded';
      this.selecting = false;
      this.loadError = '';
      // Reflect the single-loaded-model state locally without another round trip.
      const loadedId = (meta && meta.id) || this.selectedModel;
      this.lmModels = this.lmModels.map((m) => ({ ...m, loaded: m.id === loadedId }));
    },
    failLoad(token, message) {
      if (token !== this.loadToken) return; // superseded by a newer attempt — ignore
      this.stopPolling();
      this.selecting = false;
      this.loadPhase = 'idle';
      this.loadError = message;
    },
    async onSaveClick() {
      const valid = await this.$refs.validationObserver.validate();
      if (!valid.valid) return;
      if (this.isDynamicModels && (!this.modelMeta || this.modelMeta.id !== this.selectedModel)) {
        this.showToastMessage({ title: 'Select and load a model before saving.', type: 'error' });
        return;
      }
      this.loading = true;
      const config = {
        key: this.key,
        enabled: this.enabled,
        url: this.url,
        account_id: this.account_id,
        account_email: this.account_email,
      };
      if (this.isDynamicModels) {
        config.model = this.selectedModel;
        config.model_meta = this.modelMeta;
      }
      const base = { project_id: this.projectId, config };
      const response = this.editData ? await this.updateProjectAIProvider({ ...base, provider: this.editData.provider }) : await this.createProjectAIProvider({ ...base, provider: this.provider });
      if (response) {
        this.$emit('close');
      }
      this.loading = false;
    },
    onCancelClick() {
      this.$emit('close');
    },
  },
};
</script>
